import { isNavigationFailure } from '~/utils/analytics/isNavigationFailure';
import { extractTopicData } from '~/utils/analytics/extractTopicData';
import { buildUrlString } from '~/utils/buildUrlString';
import { pushCachedRequestContextToDataLayer } from '~/utils/requestContext/pushRequestContext';
import type { NavigationFailure } from 'vue-router';
import type { UseContentfulPageResponseModel } from '~/composables/useContentfulPage';
import { useContentfulPayloadForRoute } from '~/composables/useContentfulPayloadForRoute';
import { calculateTitle } from '~/composables/useMetadata.ts';

declare global {
  type EventInfoModel = {
    category: string;
    action: string;
    label: string;
  };

  type AnalyticsEventModel =
    | {
        event: 'customEvent' | string;
        eventInfo: EventInfoModel;
      }
    | object;
}

function isEventCreatedByNinetailed(event: string) {
  return event === 'nt_experience';
}

export default defineNuxtPlugin({
  name: 'analytics',
  setup() {
    const router = useRouter();
    const {
      $shopStore,
      $localeSlugMap,
      $ninetailed,
      $contentfulPagePayload,
      $globalPageSettings,
      $activeLocale
    } = useNuxtApp();
    const { user } = useShopAuth();

    // inspired by https://github.com/gtm-support/vue-gtm/blob/main/src/index.ts
    router.afterEach((to, from, failure) => {
      // duplicate route navigation? ignore.
      if (to.fullPath === from.fullPath) {
        return;
      }
      if (typeof to.name !== 'string') {
        return;
      }
      if (
        isNavigationFailure(
          failure as NavigationFailure,
          4 /* NAVIGATION_ABORTED */
        ) ||
        isNavigationFailure(
          failure as NavigationFailure,
          8 /* NAVIGATION_CANCELLED */
        )
      ) {
        return;
      }

      // inspired by https://github.com/MatteoGabriele/vue-gtag/blob/master/src/add-routes-tracker.js
      return nextTick(async () => {
        pushCachedRequestContextToDataLayer();

        // The shared payload may still belong to the previous page; only use it
        // once it matches `to`.
        const result = await resolvePayloadForRoute(to);
        if (result.status === 'mismatch') {
          console.warn(`Discard the GTM events for route »${to.path}«`);
          return;
        }

        const page =
          result.status === 'matched' ? result.payload.page : undefined;

        if (page?.fields?.format) {
          analytics.pushToDataLayer({
            page_title: calculateTitle(
              page,
              $globalPageSettings.value?.companyName ?? ''
            )
          });
          analytics.pushToDataLayer({
            page_format: page.fields.format
          });
        }
        analytics.pushToDataLayer({ page_path: to.fullPath });
        analytics.pushToDataLayer({ ecom_region: $shopStore.value });
        analytics.pushToDataLayer({
          user_id: user?.value?.claims?.oid ?? 'null'
        });
        // Topic tracking is handled by the watch below when page data is available
      });
    });

    const analytics = {
      pushToDataLayer(event: AnalyticsEventModel) {
        window.dataLayer?.push(event);
        try {
          const ninetailed = $ninetailed.value;
          if (ninetailed) {
            const copiedEvent = JSON.parse(JSON.stringify(event));
            if (
              copiedEvent &&
              copiedEvent.event &&
              !isEventCreatedByNinetailed(copiedEvent.event)
            ) {
              ninetailed.track(copiedEvent.event, {
                eventInformation: copiedEvent
              });
            }
          }
        } catch (e) {
          console.error('Ninetailed track error', e);
        }
      },
      getReferrer: () => {
        // this seems to be undocumented. Probably unstable?
        // @see https://stackoverflow.com/questions/53788975/vue-router-how-to-get-previous-page-url
        return router.options.history.state.back?.toString();
      }
    };

    const resolvePayloadForRoute = useContentfulPayloadForRoute();

    const pushTopicsAndFormat = (
      payload: UseContentfulPageResponseModel
    ): void => {
      const page = payload.page;
      if (!page) {
        return;
      }

      const topicData = extractTopicData(
        page,
        $activeLocale.value.contentfulCode,
        payload.topicSlugMapEn ?? {}
      );

      if (
        topicData.topic_slugs.length ||
        topicData.topic_ids.length ||
        topicData.topic_slugs_en.length
      ) {
        const topicPayload: Record<string, string[] | string> = {
          event: 'topics'
        };

        if (topicData.topic_slugs.length) {
          topicPayload.topic_slugs = topicData.topic_slugs;
        }
        if (topicData.topic_slugs_en.length) {
          topicPayload.topic_slugs_en = topicData.topic_slugs_en;
        }
        if (topicData.topic_ids.length) {
          topicPayload.topic_ids = topicData.topic_ids;
        }

        analytics.pushToDataLayer(topicPayload);
      }

      if (page.fields?.format) {
        analytics.pushToDataLayer({
          page_format: page.fields.format
        });
      }
    };

    // The page payload and `$localeSlugMap` (copied by the page component after
    // its re-render) change in different flush cycles. Only push once the
    // payload has been resolved for the current route, so slug, topics and
    // format always belong to the same page.
    let lastPushedKey: string | undefined;

    watch(
      [
        () => $localeSlugMap.value?.en,
        () => $contentfulPagePayload.value?.localeSlugMap?.en,
        () => $contentfulPagePayload.value?.page?.sys?.id
      ],
      async () => {
        const route = router.currentRoute.value;
        const result = await resolvePayloadForRoute(route);

        // A newer navigation took over while waiting for the fetch.
        if (router.currentRoute.value.fullPath !== route.fullPath) {
          return;
        }

        if (result.status === 'mismatch') {
          lastPushedKey = undefined;
          return;
        }

        const enSlug =
          result.status === 'matched'
            ? (result.payload.localeSlugMap?.en ?? $localeSlugMap.value?.en)
            : $localeSlugMap.value?.en;
        const pageId =
          result.status === 'matched' ? result.payload.page?.sys?.id : '';

        const key = `${route.fullPath}|${pageId ?? ''}|${enSlug ?? ''}`;
        if (key === lastPushedKey || !enSlug) {
          return;
        }
        lastPushedKey = key;

        // Push English path
        analytics?.pushToDataLayer({
          page_path_en: buildUrlString('en', enSlug)
        });

        if (result.status === 'matched') {
          pushTopicsAndFormat(result.payload);
        }
      },
      { immediate: true }
    );

    return {
      provide: {
        analytics
      }
    };
  }
});
